/** Structured chapter block model + legacy markdown/plain-text adapters. */

export const BLOCK_TYPES = [
  "chapter_title", "subtitle", "heading_1", "heading_2", "heading_3",
  "paragraph", "bullet_list", "numbered_list", "quote", "callout",
  "checklist", "table", "image", "caption", "divider", "page_break", "diagram",
];

export function newBlockId() {
  return `block_${Math.random().toString(36).slice(2, 10)}`;
}

export function makeBlock(type, content = {}, style = {}, extras = {}) {
  const now = new Date().toISOString();
  return {
    id: extras.id || newBlockId(),
    type,
    content: content || {},
    style: style || {},
    sources: extras.sources || [],
    metadata: {
      createdAt: extras.createdAt || now,
      updatedAt: now,
      ...(extras.metadata || {}),
    },
  };
}

/** Convert structured blocks back to plain markdown for legacy export/API. */
export function blocksToPlainText(blocks = []) {
  return (blocks || []).map((b) => {
    const c = b.content || {};
    switch (b.type) {
      case "chapter_title": return `# ${c.text || ""}`;
      case "subtitle": return `*${c.text || ""}*`;
      case "heading_1": return `## ${c.text || ""}`;
      case "heading_2": return `### ${c.text || ""}`;
      case "heading_3": return `#### ${c.text || ""}`;
      case "paragraph": return c.text || "";
      case "quote": return `> ${c.text || ""}${c.attribution ? `\n> — ${c.attribution}` : ""}`;
      case "callout": return `> **${c.label || "NOTE"}:** ${c.text || ""}`;
      case "bullet_list": return (c.items || []).map((i) => `- ${i}`).join("\n");
      case "numbered_list": return (c.items || []).map((i, n) => `${n + 1}. ${i}`).join("\n");
      case "checklist": return (c.items || []).map((i) => `- [ ] ${typeof i === "string" ? i : i.text || ""}`).join("\n");
      case "table": {
        const rows = c.rows || [];
        if (!rows.length) return "";
        const head = `| ${(rows[0] || []).join(" | ")} |`;
        const sep = `| ${(rows[0] || []).map(() => "---").join(" | ")} |`;
        const body = rows.slice(1).map((r) => `| ${(r || []).join(" | ")} |`).join("\n");
        return [head, sep, body].filter(Boolean).join("\n");
      }
      case "image": return `![${c.alt || c.caption || "image"}](${c.url || c.src || ""})`;
      case "caption": return `_${c.text || ""}_`;
      case "divider": return "---";
      case "page_break": return "\n<!-- page break -->\n";
      case "diagram": return `[DIAGRAM: ${c.title || "Diagram"} — ${(c.nodes || []).join(" → ")}]`;
      default: return typeof c.text === "string" ? c.text : "";
    }
  }).filter((s) => s != null && String(s).trim() !== "").join("\n\n");
}

function splitListItems(body) {
  return body.split(/\n/).map((l) => l.replace(/^[-*+]\s+/, "").replace(/^\d+\.\s+/, "").replace(/^\[\s?[xX ]\]\s*/, "").trim()).filter(Boolean);
}

/** Parse markdown / plain text into structured blocks without destroying content. */
export function legacyContentToBlocks(content, { chapterTitle } = {}) {
  const text = String(content || "").replace(/\r\n/g, "\n").trim();
  const blocks = [];
  if (chapterTitle) {
    blocks.push(makeBlock("chapter_title", { text: chapterTitle }));
  }
  if (!text) {
    if (!blocks.length) blocks.push(makeBlock("paragraph", { text: "" }));
    return blocks;
  }

  const chunks = text.split(/\n{2,}/);
  for (const raw of chunks) {
    const chunk = raw.trim();
    if (!chunk) continue;

    const diagramMatch = chunk.match(/^\[DIAGRAM:\s*(.+?)\]$/i);
    if (diagramMatch) {
      const inner = diagramMatch[1];
      const parts = inner.split(/—|–|-/).map((s) => s.trim());
      const title = parts[0] || "Diagram";
      const nodes = (parts[1] || "").split(/→|->|›/).map((s) => s.trim()).filter(Boolean);
      blocks.push(makeBlock("diagram", { title, nodes: nodes.length ? nodes : [title], diagramType: "roadmap" }));
      continue;
    }
    const doodleMatch = chunk.match(/^\[(?:DOODLE|VISUAL PLACEMENT|IMAGE):\s*(.+?)\]$/i);
    if (doodleMatch) {
      blocks.push(makeBlock("image", {
        prompt: doodleMatch[1],
        caption: doodleMatch[1],
        alt: doodleMatch[1],
        url: "",
        placement: "inline",
        status: "planned",
      }));
      continue;
    }

    if (/^<!--\s*page break\s*-->$/i.test(chunk) || /^={3,}$/.test(chunk)) {
      blocks.push(makeBlock("page_break", {}));
      continue;
    }
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(chunk)) {
      blocks.push(makeBlock("divider", {}));
      continue;
    }
    if (/^#\s+/.test(chunk) && !chapterTitle) {
      blocks.push(makeBlock("chapter_title", { text: chunk.replace(/^#\s+/, "") }));
      continue;
    }
    if (/^##\s+/.test(chunk)) {
      blocks.push(makeBlock("heading_1", { text: chunk.replace(/^##\s+/, "") }));
      continue;
    }
    if (/^###\s+/.test(chunk)) {
      blocks.push(makeBlock("heading_2", { text: chunk.replace(/^###\s+/, "") }));
      continue;
    }
    if (/^####\s+/.test(chunk)) {
      blocks.push(makeBlock("heading_3", { text: chunk.replace(/^####\s+/, "") }));
      continue;
    }
    if (/^>\s*/.test(chunk)) {
      const lines = chunk.split(/\n/).map((l) => l.replace(/^>\s?/, ""));
      const joined = lines.join("\n");
      const callout = joined.match(/^\*\*([^*]+):\*\*\s*(.+)$/s);
      if (callout) {
        blocks.push(makeBlock("callout", { label: callout[1], text: callout[2] }, { variant: "information" }));
      } else {
        const attr = joined.match(/^(.+)\n—\s*(.+)$/s);
        blocks.push(makeBlock("quote", {
          text: attr ? attr[1].trim() : joined,
          attribution: attr ? attr[2].trim() : "",
        }));
      }
      continue;
    }
    if (/^[-*+]\s+\[/.test(chunk) || /\n[-*+]\s+\[/.test(chunk)) {
      blocks.push(makeBlock("checklist", { items: splitListItems(chunk) }));
      continue;
    }
    if (/^[-*+]\s+/.test(chunk) || /\n[-*+]\s+/.test(chunk)) {
      blocks.push(makeBlock("bullet_list", { items: splitListItems(chunk) }));
      continue;
    }
    if (/^\d+\.\s+/.test(chunk) || /\n\d+\.\s+/.test(chunk)) {
      blocks.push(makeBlock("numbered_list", { items: splitListItems(chunk) }));
      continue;
    }
    const img = chunk.match(/^!\[([^\]]*)\]\(([^)]+)\)/);
    if (img) {
      blocks.push(makeBlock("image", { alt: img[1], caption: img[1], url: img[2], placement: "inline", status: "ready" }));
      continue;
    }
    blocks.push(makeBlock("paragraph", { text: chunk }));
  }

  if (!blocks.length) blocks.push(makeBlock("paragraph", { text }));
  return blocks;
}

/** Resolve blocks for a chapter: prefer saved blocks, else migrate legacy content. */
export function resolveChapterBlocks(chapter) {
  if (!chapter) return [makeBlock("paragraph", { text: "" })];
  if (Array.isArray(chapter.blocks) && chapter.blocks.length) {
    return chapter.blocks.map((b) => ({
      ...b,
      id: b.id || newBlockId(),
      content: b.content || {},
      style: b.style || {},
      sources: b.sources || [],
      metadata: b.metadata || {},
    }));
  }
  return legacyContentToBlocks(chapter.content || chapter.plain_text_content || "", {
    chapterTitle: chapter.title,
  });
}

export function updateBlockInList(blocks, blockId, patch) {
  return (blocks || []).map((b) => {
    if (b.id !== blockId) return b;
    return {
      ...b,
      ...patch,
      content: patch.content !== undefined ? { ...b.content, ...patch.content } : b.content,
      style: patch.style !== undefined ? { ...b.style, ...patch.style } : b.style,
      sources: patch.sources !== undefined ? patch.sources : b.sources,
      metadata: { ...(b.metadata || {}), updatedAt: new Date().toISOString() },
    };
  });
}

export function applyStyleToBlocks(blocks, stylePatch, predicate = () => true) {
  return (blocks || []).map((b) => {
    if (!predicate(b)) return b;
    return {
      ...b,
      style: { ...(b.style || {}), ...stylePatch },
      metadata: { ...(b.metadata || {}), updatedAt: new Date().toISOString() },
    };
  });
}

export function wordCountFromBlocks(blocks = []) {
  const text = blocksToPlainText(blocks);
  const t = text.trim();
  return t ? t.split(/\s+/).length : 0;
}

export function countBlockType(blocks, type) {
  return (blocks || []).filter((b) => b.type === type).length;
}

/** Extract simple factual-looking sentences for claim review (heuristic, honest). */
export function extractClaimsFromBlocks(blocks = []) {
  const claims = [];
  const claimy = /\b(must|required|illegal|legal|percent|%|according to|regulation|license|FDA|USDA|statute|law|always|never|prohibits)\b/i;
  (blocks || []).forEach((b) => {
    const text = b.content?.text || (Array.isArray(b.content?.items) ? b.content.items.join(" ") : "");
    if (!text || typeof text !== "string") return;
    text.split(/(?<=[.!?])\s+/).forEach((sentence, i) => {
      const s = sentence.trim();
      if (s.length < 24) return;
      if (!claimy.test(s)) return;
      claims.push({
        id: `claim_${b.id}_${i}`,
        blockId: b.id,
        text: s,
        status: "needs_review",
        sourceIds: (b.sources || []).map((x) => x.sourceId).filter(Boolean),
        reviewedAt: null,
      });
    });
  });
  return claims;
}

export const DEFAULT_BOOK_STYLE_TOKENS = {
  body: {
    fontFamily: "Georgia, 'Times New Roman', serif",
    fontSize: 18,
    lineHeight: 1.75,
    paragraphSpacing: 18,
    color: "#172019",
    alignment: "left",
    firstLineIndent: 0,
  },
  chapter_title: {
    fontFamily: "Inter, system-ui, sans-serif",
    fontSize: 36,
    fontWeight: 800,
    color: "#0f1c18",
    alignment: "left",
    letterSpacing: -0.02,
    spacingAbove: 0,
    spacingBelow: 20,
  },
  heading_1: {
    fontFamily: "Inter, system-ui, sans-serif",
    fontSize: 28,
    fontWeight: 700,
    color: "#14241e",
    alignment: "left",
    spacingAbove: 28,
    spacingBelow: 12,
  },
  heading_2: {
    fontFamily: "Inter, system-ui, sans-serif",
    fontSize: 22,
    fontWeight: 650,
    color: "#1a2c25",
    alignment: "left",
    spacingAbove: 22,
    spacingBelow: 10,
  },
  quote: {
    fontFamily: "Georgia, serif",
    fontStyle: "italic",
    borderColor: "#43d895",
    background: "rgba(67,216,149,0.08)",
    padding: 16,
    alignment: "left",
  },
  callout: {
    variant: "information",
    background: "#f3faf6",
    borderColor: "#43d895",
    borderWidth: 4,
    padding: 14,
    cornerRadius: 10,
  },
  table: {
    headerBackground: "rgba(67,216,149,0.12)",
    headerTextColor: "#0f5c42",
    alternateRow: "rgba(0,0,0,0.03)",
    borderColor: "rgba(0,0,0,0.12)",
    cellPadding: 10,
    fontSize: 14,
  },
};

export const CALLOUT_VARIANTS = [
  { id: "attention", label: "Attention", background: "#fff8e8", borderColor: "#e3b84f" },
  { id: "warning", label: "Warning", background: "#f8e7df", borderColor: "#b86842" },
  { id: "action", label: "Action", background: "#e8f6ef", borderColor: "#1ebc78" },
  { id: "founder_note", label: "Founder Note", background: "#efeaf8", borderColor: "#8b6cf4" },
  { id: "money_at_risk", label: "Money at Risk", background: "#fde8e8", borderColor: "#ee7373" },
  { id: "information", label: "Information", background: "#eaf4fb", borderColor: "#4a90c8" },
  { id: "success", label: "Success", background: "#e7f8ef", borderColor: "#2ed58d" },
  { id: "checklist", label: "Checklist", background: "#f4f7f5", borderColor: "#6d857a" },
];
